import { DataLayerSearch, DataLayerSearchFilter, Job, UserObject } from "./interfaces";
import { Params } from "./search-params";
import { getUserObject } from "./session/sessionmain";
import { clearAuthenticationAttribution } from "./attribution";

type ApplyJobData = Pick<Job,
    'Agency'
    | 'Department'
    | 'Title'
    | 'SubAgency'
    | 'JobGrade'
    | 'LowGrade'
    | 'HighGrade'
    | 'DocumentID'
    | 'JobCategoryCode'>;

declare global {
    interface Window {
        dataLayer: any[];
    }
}

function waitForDataLayer(maxChecks = 50, intervalInMS = 100): Promise<boolean> {
    return new Promise((resolve) => {
        let checkDataLayerAttempts = 1;

        const checkDataLayer = setInterval(() => {
            if (window.dataLayer) {
                clearInterval(checkDataLayer);
                resolve(true);
            }
            else if (checkDataLayerAttempts++ >= maxChecks) {
                clearInterval(checkDataLayer);
                console.debug('window.dataLayer wait timed out.');
                resolve(false);
            }
        }, intervalInMS);
    });
}

function waitForUserIdInDataLayer(maxChecks = 50, intervalInMS = 100): Promise<boolean> {
    return new Promise((resolve) => {
        let checkUserIdAttempts = 1;

        const checkUserId = setInterval(() => {
            const userObject = getUserObject();
            if (userObject && !userObject.IsAuthenticated) {
                clearInterval(checkUserId);
                resolve(true);
                return;
            }
            const hasUserId = window.dataLayer?.some(entry => entry?.user_id);
            if (hasUserId) {
                clearInterval(checkUserId);
                resolve(true);
            }
            else if (checkUserIdAttempts++ >= maxChecks) {
                clearInterval(checkUserId);
                console.debug('window.dataLayer user_id wait timed out.');
                resolve(false);
            }
        }, intervalInMS);
    });
}

export function pushAnalyticsID() {
    window.dataLayer = window.dataLayer || [];
    const userObject = getUserObject();

    if (!userObject.IsAuthenticated || !userObject.AnalyticsID) {
        document.dispatchEvent(new Event("analytics-id-ready"));
        return;
    }

    window.dataLayer.push({ "user_id": userObject.AnalyticsID });
    const attribution = userObject.AuthenticationAttribution;
    if (attribution) {
        const campaignDetails = {
            campaign_id: attribution.UtmID,
            source: attribution.UtmSource ?? attribution.ReferrerHost ?? '(direct)',
            medium: attribution.UtmMedium ?? (attribution.ReferrerHost ? 'referral' : '(none)'),
            campaign: attribution.UtmCampaign,
            term: attribution.UtmTerm,
            content: attribution.UtmContent,
            landing_path: attribution.LandingPath,
            referrer_host: attribution.ReferrerHost
        };
        window.dataLayer.push({ event: "campaign_details", ...campaignDetails });
        if (attribution.IsAccountCreated) {
            window.dataLayer.push({ event: "account_created", ...campaignDetails });
        }
        delete userObject.AuthenticationAttribution;
        sessionStorage.setItem("userObject", JSON.stringify(userObject));
        clearAuthenticationAttribution();
    }
    document.dispatchEvent(new Event("analytics-id-ready"));
}

function getDataLayerObjectForSearch(urlObject: Params, userObject: UserObject, event: string, setFilters: boolean, isAutocomplete: boolean): DataLayerSearch {
    const SearchTermParams = ['k', 'soc', 'jt', 'j', 'a', 'Keyword', 'IsAuthenticated', 'UniqueSearchID', 'Page', 'ResultsPerPage'];
    let dataLayerObject: DataLayerSearch = {
        "event": event,
        "searchId": userObject.UniqueSearch.Id,
        "authenticated": userObject.IsAuthenticated,
        "search_term": urlObject['Keyword'] ?? "",
        "filters": { "filterId": userObject.UniqueSearch.filters["filterId"] ?? crypto.randomUUID() },
        "total_results": urlObject.TotalResults,
        "autocomplete": isAutocomplete
    };
    for (let key in urlObject) {
        if (SearchTermParams.indexOf(key) > -1 || key === 'TotalResults') {
            continue;
        }
        let value = urlObject[key];
        if (value !== undefined && value !== null && ((Array.isArray(value) && value.length > 0) || value.toString().length > 0)) {
            dataLayerObject.filters[key] = value;
        }
    }
    if (setFilters === true) {
        userObject.UniqueSearch["filters"] = dataLayerObject.filters;
        userObject.UniqueSearch.filters["filterId"] = crypto.randomUUID();
        dataLayerObject.filters.filterId = userObject.UniqueSearch.filters.filterId;
        sessionStorage.setItem("userObject", JSON.stringify(userObject));
    }
    return dataLayerObject;
}

export async function logSearch(urlObject: Params, isAutocomplete: boolean) {
    const isReady = await waitForDataLayer();

    if (isReady) {
        window.dataLayer.push(getDataLayerObjectForSearch(urlObject, getUserObject(), "view_search_results", true, isAutocomplete));
        console.debug(window.dataLayer);
    }
}

export async function logSearchFilterInteraction(urlObject: Params, isAutocomplete: boolean) {
    const isReady = await waitForDataLayer();

    if (isReady) {
        const userObject = getUserObject();
        let filterId = userObject.UniqueSearch.filters.filterId;
        const dataLayerSearchFilters: DataLayerSearchFilter = {
            ...getDataLayerObjectForSearch(urlObject, userObject, "filter_interaction", false, isAutocomplete),
            filter_added: false,
            filter_removed: false,
            filters_changed: []
        };

        for (const prop in userObject.UniqueSearch.filters) {
            if (prop === 'filterId' || prop === 'TotalResults') {
                continue;
            }
            const dataLayerFilter = dataLayerSearchFilters.filters[prop];
            const uoFilter = userObject.UniqueSearch.filters[prop];
            if (dataLayerFilter === undefined || (Array.isArray(dataLayerFilter) && dataLayerFilter.length < uoFilter.length)) {
                dataLayerSearchFilters.filter_removed = true;
                if (dataLayerSearchFilters.filters_changed?.indexOf(prop) === -1) {
                    dataLayerSearchFilters.filters_changed.push(prop);
                    dataLayerSearchFilters.filters[prop] = Array.isArray(dataLayerFilter) ? dataLayerFilter : undefined;
                }
            }
            else if (dataLayerFilter != undefined && !Array.isArray(dataLayerFilter) && uoFilter != dataLayerFilter && dataLayerSearchFilters.filters_changed?.indexOf(prop) === -1) {
                dataLayerSearchFilters.filters_changed.push(prop);
            }
        }

        for (const prop in dataLayerSearchFilters.filters) {
            if (prop === 'filterId' || prop === 'TotalResults') {
                continue;
            }
            const dataLayerFilter = dataLayerSearchFilters.filters[prop];
            const uoFilter = userObject.UniqueSearch.filters[prop];
            if (uoFilter === undefined || (Array.isArray(uoFilter) && dataLayerFilter?.length > uoFilter.length)) {
                dataLayerSearchFilters.filter_added = true;
                if (dataLayerSearchFilters.filters_changed?.indexOf(prop) === -1) {
                    dataLayerSearchFilters.filters_changed.push(prop);
                }
            }
            else if (uoFilter != undefined && !Array.isArray(uoFilter) && uoFilter != dataLayerFilter && dataLayerSearchFilters.filters_changed?.indexOf(prop) === -1) {
                dataLayerSearchFilters.filters_changed.push(prop);
            }
        }

        if (dataLayerSearchFilters.filters_changed.length > 0) {
            filterId = crypto.randomUUID();
            userObject.UniqueSearch.filters = dataLayerSearchFilters.filters;
            userObject.UniqueSearch.filters.filterId = filterId;
            dataLayerSearchFilters.filters.filterId = filterId;
            sessionStorage.setItem('userObject', JSON.stringify(userObject));
            window.dataLayer.push(dataLayerSearchFilters);
            console.debug(window.dataLayer);
        }
    }
}

export async function logSearchConversion(userObject: UserObject, event: string) {
    const isReady = await waitForDataLayer();

    if (isReady && userObject.UniqueSearch && userObject.SearchResults) {
        const dataLayerObject = {
            event: event,
            searchId: userObject.UniqueSearch.Id,
            filterId: userObject.UniqueSearch.filters.filterId,
            conversionId: userObject.SearchResults.ConversionId,
            selectedDocumentId: userObject.SearchResults.SelectedDocumentId,
            selectedDocumentPage: userObject.SearchResults.Results.PageSets[0].Page,
            selectedDocumentRank: userObject.SearchResults.Results.Items.indexOf(userObject.SearchResults.SelectedDocumentId) + 1
        }
        window.dataLayer.push(dataLayerObject);
        console.debug(window.dataLayer);
    }
}

export async function logSaveJob(joa: Job, isSaving: boolean) {
    const [isReady, hasUserId] = await Promise.all([waitForDataLayer(), waitForUserIdInDataLayer()]);

    if (isReady && hasUserId) {
        const dataLayerObject = {
            event: 'save_job_click',
            searchId: getUserObject()?.UniqueSearch?.Id,
            saved: isSaving,
            agency: joa.Agency,
            department: joa.Department,
            title: joa.Title,
            series: joa.JobCategoryCode?.map(x => x.Code).join("|"),
            office: joa.SubAgency,
            grade: `${joa.JobGrade}${joa.LowGrade}-${joa.HighGrade}`,
            usaj_control_number: joa.DocumentID
        }
        window.dataLayer.push(dataLayerObject);
        console.debug('dataLayer', window.dataLayer);
    }
}

export async function logApplyJob(joa: ApplyJobData) {
    const [isReady, hasUserId] = await Promise.all([waitForDataLayer(), waitForUserIdInDataLayer()]);
    let series = '';

    if (joa.JobCategoryCode?.length > 0 && typeof(joa.JobCategoryCode[0]) === 'string') {
        series = joa.JobCategoryCode.join('|');
    }
    else {
        series = joa.JobCategoryCode?.map(j => j.Code).join('|');
    }

    if (isReady && hasUserId) {
        const dataLayerObject = {
            event: 'apply_job_click',
            searchId: getUserObject()?.UniqueSearch?.Id,
            agency: joa.Agency,
            department: joa.Department,
            title: joa.Title,
            series: series,
            office: joa.SubAgency,
            grade: `${joa.JobGrade}${joa.LowGrade}-${joa.HighGrade}`,
            usaj_control_number: joa.DocumentID
        }
        window.dataLayer.push(dataLayerObject);
        console.debug('dataLayer', window.dataLayer);
    }
}

export async function logJobDetails(joa: Job, userObject: UserObject) {
    const isReady = await waitForDataLayer();

    if (isReady) {
        const dataLayerObject = {
            event: 'job_details',
            searchId: userObject.UniqueSearch?.Id,
            agency: joa.Agency,
            department: joa.Department,
            title: joa.Title,
            series: joa.JobCategoryCode?.map(x => x.Code).join("|"),
            office: joa.SubAgency,
            grade: `${joa.JobGrade}${joa.LowGrade}-${joa.HighGrade}`,
            filterId: userObject.UniqueSearch?.filters?.filterId,
            conversionId: userObject.SearchResults?.ConversionId,
            usaj_control_number: joa.DocumentID
        }
        window.dataLayer.push(dataLayerObject);
        console.debug('dataLayer', window.dataLayer);
    }
}
